// src/components/ui/navigation/Sidebar/SidebarShadcn.jsx
import React from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import {
  Home,
  Calendar,
  CalendarCheck,
  Users,
  FileText,
  Workflow,
  Handshake,
  Building2,
  MessageSquare,
  Wrench,
  Code,
  LogOut,
  Star
} from 'lucide-react';

// Import shadcn sidebar components
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar
} from '../../sidebar';

// Import contexts
import { useAuth } from '../../../../contexts/AuthContext.jsx';
import { cn } from '../../../../utils/cn';

// Import UniversalHeader
import UniversalHeader from '../../layout/UniversalHeader/UniversalHeader';

// FIXED: Clean SidebarMenuButton with colors on NavLink
const SidebarNavLink = ({ to, icon: Icon, label, end = false, variant = 'primary' }) => {
  // Variant styles for different navigation sections - COLORS ONLY
  const variantStyles = {
    primary: {
      active: "bg-gradient-to-r from-blue-600/20 to-blue-500/10 text-blue-300",
      inactive: "text-slate-300 hover:text-white hover:bg-slate-800/50"
    },
    config: {
      active: "bg-gradient-to-r from-emerald-600/20 to-emerald-500/10 text-emerald-300",
      inactive: "text-slate-300 hover:text-white hover:bg-slate-800/50"
    }
  };

  const currentVariant = variantStyles[variant];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={label}>
        <NavLink
          to={to}
          end={end}
          className={({ isActive }) => 
            isActive ? currentVariant.active : currentVariant.inactive
          }
        >
          <Icon />
          <span>{label}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

// FIXED: User Profile using exact shadcn pattern
const SidebarUserProfileShadcn = ({ user, onLogout }) => {
  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2);
  };

  return (
    <SidebarFooter className="bg-slate-900/50">
      <SidebarMenu>
        <SidebarMenuItem>
          {/* User Profile Card - using exact shadcn structure */}
          <SidebarMenuButton size="lg" className="bg-slate-800/50 hover:bg-slate-700/50">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold aspect-square size-8 items-center justify-center rounded-lg flex">
              {getUserInitials(user?.name)}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium text-white">
                {user?.name || 'User'}
              </span>
              <span className="truncate text-xs text-slate-400">
                {user?.email}
              </span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>

        {/* Logout Button - clean shadcn button */}
        <SidebarMenuItem>
          <SidebarMenuButton onClick={onLogout} className="text-slate-300 hover:text-white hover:bg-slate-700/50">
            <LogOut />
            <span>Sign Out</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
};

// Main Sidebar Component
const SidebarContentComponent = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Navigation items configuration
  const navItems = [
    {
      to: '/dashboard',
      icon: Home,
      label: 'Dashboard',
      end: true
    },
    {
      to: '/calendar',
      icon: Calendar,
      label: 'Calendar'
    },
    {
      to: '/appointments',
      icon: CalendarCheck,
      label: 'Appointments'
    },
    {
      to: '/customers',
      icon: Users,
      label: 'Customers'
    },
    {
      to: '/invoices',
      icon: FileText,
      label: 'Invoices'
    },
    {
      to: '/reviews',
      icon: Star,
      label: 'Reviews'
    },
    {
      to: '/pipelines',
      icon: Workflow,
      label: 'Automation'
    },
    {
      to: '/widgets',
      icon: Code,
      label: 'Website Integration'
    },
    {
      to: '/partners',
      icon: Handshake,
      label: 'Partners'
    }
  ];

  // Configuration items (admin only)
  const configItems = isAdmin ? [
    {
      to: '/configuration/business',
      icon: Building2,
      label: 'Business'
    },
    {
      to: '/configuration/communications',
      icon: MessageSquare,
      label: 'Communications'
    },
    {
      to: '/configuration/services',
      icon: Wrench,
      label: 'Services'
    },
    {
      to: '/configuration/team',
      icon: Users,
      label: 'Team'
    }
  ] : [];

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        "bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900",
        "border-r border-slate-700/50 backdrop-blur-xl",
        "shadow-2xl shadow-black/25"
      )}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
      
      <SidebarHeader>
        <SidebarTrigger className="text-slate-400 hover:text-slate-200" />
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  end={item.end}
                  variant="primary"
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Configuration Section - Admin Only */}
        {isAdmin && configItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-slate-400 uppercase tracking-wide">
              Configuration
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {configItems.map((item) => (
                  <SidebarNavLink
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    variant="config"
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
};

// Wrapper component that includes the provider and handles children
const SidebarShadcn = ({ children }) => {
  return (
    <SidebarProvider defaultOpen={true}>
      <SidebarContentComponent />
      <SidebarInset>
        <UniversalHeader />
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SidebarShadcn;